import {getEnvironment} from './api';
import {debugLog, debugWarn} from './debug';

/**
 * Paddle Billing (v2) integration.
 *
 * The retired marketing site used the `@paddle/paddle-js` npm wrapper. We load Paddle.js from its
 * CDN instead so the app takes no new dependency — the wrapper does exactly this under the hood.
 * Everything Paddle-related is confined to this file; swapping back to the npm package means
 * rewriting `loadPaddle()` and nothing else.
 */

export type PaddleEnvironment = 'sandbox' | 'production';

export interface PaddleCheckoutEvent {
    name: string;
    data?: unknown;
}

interface PaddleGlobal {
    Environment: { set: (env: PaddleEnvironment) => void };
    Initialize: (options: {
        token: string;
        eventCallback?: (event: PaddleCheckoutEvent) => void;
    }) => void;
    Update: (options: { pwCustomer?: { id: string } }) => void;
    Checkout: {
        open: (options: {
            items: Array<{ priceId: string; quantity: number }>;
            customer?: { id: string } | { email: string };
            customData?: Record<string, unknown>;
            settings?: Record<string, unknown>;
        }) => void;
    };
}

const PADDLE_CDN_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

/**
 * Paddle's sandbox is used for local dev and staging; only production hits live billing.
 *
 * This reads the same `getEnvironment()` the API origins are derived from, so billing and the API
 * can never end up pointed at different environments.
 */
export function getPaddleEnvironment(): PaddleEnvironment {
  return getEnvironment() === 'production' ? 'production' : 'sandbox';
}

function getPaddleGlobal(): PaddleGlobal | null {
  return (window as any).Paddle ?? null;
}

let scriptPromise: Promise<PaddleGlobal> | null = null;

function loadPaddle(): Promise<PaddleGlobal> {
  const existing = getPaddleGlobal();
  if (existing) {
    return Promise.resolve(existing);
  }

  if (!scriptPromise) {
    scriptPromise = new Promise<PaddleGlobal>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PADDLE_CDN_URL;
      script.async = true;
      script.onload = () => {
        const paddle = getPaddleGlobal();
        if (paddle) {
          resolve(paddle);
        } else {
          reject(new Error('Paddle.js loaded but window.Paddle is missing'));
        }
      };
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Paddle.js. Check the Content-Security-Policy script-src.'));
      };
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

let initPromise: Promise<PaddleGlobal | null> | null = null;

/**
 * Load and initialise Paddle once per page load. Resolves to null when no client key is
 * configured, so checkout can fail with a clear message instead of throwing at import time.
 */
export function initializePaddle(
  eventCallback?: (event: PaddleCheckoutEvent) => void
): Promise<PaddleGlobal | null> {
  if (initPromise) {
    return initPromise;
  }

  const token = import.meta.env.VITE_PADDLE_CLIENT_KEY;
  if (!token) {
    debugWarn('[Paddle] VITE_PADDLE_CLIENT_KEY is not set — checkout is disabled');
    initPromise = Promise.resolve(null);
    return initPromise;
  }

  initPromise = loadPaddle()
    .then((paddle) => {
      const environment = getPaddleEnvironment();
      debugLog('[Paddle] Initialising in', environment);
      paddle.Environment.set(environment);
      paddle.Initialize({token, eventCallback});
      return paddle;
    })
    .catch((error) => {
      console.error('[Paddle] Initialisation failed:', error);
      initPromise = null;
      return null;
    });

  return initPromise;
}

/**
 * The already-initialised Paddle instance, or null if init never ran / failed.
 */
export function getPaddle(): Promise<PaddleGlobal | null> {
  return initPromise ?? Promise.resolve(null);
}

/**
 * Checkout events have exactly one subscriber as far as Paddle is concerned: `Initialize` takes a
 * single `eventCallback`, registered once by `PaddleInitializer`. Anything else that needs to know a
 * checkout finished — the signup flow, which has to start polling for its account — subscribes here
 * and the initializer fans events out.
 */
export type PaddleEventListener = (event: PaddleCheckoutEvent) => void;

const eventListeners = new Set<PaddleEventListener>();

export function subscribeToPaddleEvents(listener: PaddleEventListener): () => void {
  eventListeners.add(listener);
  return () => {
    eventListeners.delete(listener);
  };
}

export function emitPaddleEvent(event: PaddleCheckoutEvent) {
  eventListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error('[Paddle] Event listener threw:', error);
    }
  });
}

/**
 * Hand the Paddle customer id to ProfitWell Retain.
 */
export function updatePaddleCustomer(customerId: string) {
  const paddle = getPaddleGlobal();
  if (paddle) {
    paddle.Update({pwCustomer: {id: customerId}});
  }
}

/**
 * Open the overlay checkout. Always keyed by customer id — the site passed an email here even
 * though it had just created the customer server-side, which loses the ProfitWell association.
 */
export function openPaddleCheckout(paddle: PaddleGlobal, options: {
    priceId: string;
    customerId: string;
    userId: string;
}) {
  paddle.Checkout.open({
    items: [{priceId: options.priceId, quantity: 1}],
    customer: {id: options.customerId},
    customData: {userId: options.userId},
    settings: {theme: 'dark'},
  });
}

/**
 * The signup checkout — the one that runs *before* an account exists.
 *
 * There is no customer id to key it by yet, deliberately: the server never creates a Paddle customer
 * up front, so an abandoned signup leaves no orphan behind. Paddle creates the customer during
 * checkout instead, which is why this passes an email.
 *
 * `signup_token` is what ties the resulting subscription back to the parked email address. The email
 * is the server's fallback if the custom data does not survive the checkout, so it has to be the same
 * address that was sent to `/signup/start`.
 */
export function openSignupCheckout(paddle: PaddleGlobal, options: {
    priceId: string;
    email: string;
    signupToken: string;
}) {
  paddle.Checkout.open({
    items: [{priceId: options.priceId, quantity: 1}],
    customer: {email: options.email},
    customData: {signup_token: options.signupToken},
    settings: {theme: 'dark'},
  });
}
